import React, { useState, useEffect } from 'react';
import { Settings, BookOpen, FileCheck, Award, Clock, CreditCard, ChevronRight, LogOut, Menu, X } from 'lucide-react';
import Layout from '../shared/Layout';
import DireccionParametrizaciones from './DireccionParametrizaciones';
import DireccionAsignaturas from './DireccionAsignaturas';
import DireccionLicencias from './DireccionLicencias';
import DireccionCursos from './DireccionCursos';
import DireccionCertificados from './DireccionCertificados';
import DireccionHorarios from './DireccionHorarios';
import DireccionPagos from './DireccionPagos';
import DireccionEncuestas from './DireccionEncuestas';
import DireccionSolicitudesCambio from './DireccionSolicitudesCambio';
import DireccionEstudiantes from './DireccionEstudiantes';
import DireccionEstudiantesKardex from './DireccionEstudiantesKardex';
import DireccionHistorialAcademico from './DireccionHistorialAcademico';
import DireccionDocentes from './DireccionDocentes';
import JefaturaCursosInscritos from '../jefatura/JefaturaCursosInscritos';
import JefaturaConsultasEstudiantes from '../jefatura/JefaturaConsultasEstudiantes';
import JefaturaHistorialAcademico from '../jefatura/JefaturaHistorialAcademico';
import JefaturaConsultasAsignaturas from '../jefatura/JefaturaConsultasAsignaturas';

const DashboardDireccion = ({ onLogout, userName = 'Usuario', menuItems: externalMenuItems }) => {
    const [menuItems, setMenuItems] = useState([]);
    const [activeMenu, setActiveMenu] = useState('direccion_parametrizaciones');

    useEffect(() => {
        // Menús jerárquicos de Dirección
        const defaultMenus = [
            {
                id: 'direccion_parametrizaciones',
                label: 'Parametrizaciones',
                children: [
                    { id: 'direccion_periodos', label: 'Periodos' },
                    { id: 'direccion_pagos', label: 'Pagos' },
                ]
            },
            {
                id: 'direccion_autorizaciones',
                label: 'Autorizaciones',
                children: [
                    { id: 'direccion_asignaturas', label: 'Asignaturas' },
                    { id: 'direccion_estudiantes', label: 'Edición Estudiantes' },
                    { id: 'direccion_cambio_carrera', label: 'Cambio Carrera' },
                    { id: 'direccion_licencias', label: 'Licencias' },
                    {
                        id: 'direccion_cursos_ext', label: 'Cursos de Extensión', children: [
                            { id: 'direccion_cursos', label: 'Aprobación de Cursos' },
                            { id: 'direccion_cursos_inscritos', label: 'Inscritos' }
                        ]
                    },
                ]
            },
            { id: 'direccion_config_pagos', label: 'Configuración Pagos' },
            { id: 'direccion_encuestas', label: 'Encuestas' },
            { id: 'direccion_certificados', label: 'Certificados' },
            {
                id: 'direccion_consultas',
                label: 'Consultas',
                children: [
                    { id: 'direccion_consulta_asignaturas', label: 'Asignaturas' },
                    { id: 'direccion_consulta_historial', label: 'Historial Académico' },
                    { id: 'direccion_consulta_estudiantes', label: 'Estudiantes' },
                ]
            },
        ];

        // IDs de menú a ocultar: 73 (Config Pagos), 67 (Encuestas), 56 (Certificados)
        const hiddenMenuIds = ['73', '67', '56', 73, 67, 56, 57, '57'];

        // Función para filtrar menús recursivamente
        const filterMenuItems = (items) => {
            return items
                .filter(item => !hiddenMenuIds.includes(item.id_menu) && !hiddenMenuIds.includes(String(item.id_menu)))
                .map(item => ({
                    ...item,
                    children: item.children ? filterMenuItems(item.children) : undefined
                }));
        };

        if (externalMenuItems && externalMenuItems.length > 0) {
            const filteredMenus = filterMenuItems(externalMenuItems);
            setMenuItems(filteredMenus);
            // Buscar el primer menú clickeable (sin hijos)
            const findFirstClickable = (items) => {
                for (const item of items) {
                    if (!item.children || item.children.length === 0) return item.id;
                    const childClickable = findFirstClickable(item.children);
                    if (childClickable) return childClickable;
                }
                return null;
            };
            const firstClickable = findFirstClickable(filteredMenus);
            if (firstClickable) setActiveMenu(firstClickable);
        } else {
            // Menú por defecto sin los items ocultos
            const filteredDefaultMenus = defaultMenus.filter(m =>
                !['direccion_config_pagos', 'direccion_encuestas', 'direccion_certificados'].includes(m.id)
            );
            setMenuItems(filteredDefaultMenus);
            setActiveMenu('direccion_periodos'); // Primer item clickeable
        }
    }, [externalMenuItems]);

    const renderContent = () => {
        switch (activeMenu) {
            case 'direccion_periodos':
                return <DireccionParametrizaciones />;
            case 'direccion_pagos':
                return <DireccionPagos />;
            case 'direccion_asignaturas':
                return <DireccionAsignaturas />;
            case 'direccion_estudiantes':
                return <DireccionEstudiantes />;
            case 'direccion_horarios':
                return <DireccionHorarios />;
            case 'direccion_cambio_carrera':
                return <DireccionSolicitudesCambio />;
            case 'direccion_licencias':
                return <DireccionLicencias />;
            case 'direccion_cursos':
                return <DireccionCursos />;
            case 'direccion_config_pagos':
                return <div className="p-6"><h2 className="text-2xl font-bold text-gray-800">Configuración de Pagos</h2><p className="mt-4 text-gray-600">Configuración avanzada de pagos (próximamente)</p></div>;
            case 'direccion_encuestas':
                return <DireccionEncuestas />;
            case 'direccion_certificados':
                return <DireccionCertificados />;
            case 'direccion_parametrizaciones':
                return <DireccionParametrizaciones />;
            case 'direccion_consulta_estudiantes':
                return <JefaturaConsultasEstudiantes readOnly={true} />;
            case 'direccion_consulta_historial':
                return <JefaturaHistorialAcademico readOnly={true} />;
            case 'direccion_consulta_docentes':
                return <JefaturaConsultasAsignaturas readOnly={true} />;
            case 'direccion_cursos_inscritos':
                return <JefaturaCursosInscritos readOnly={true} />;
            default:
                return <div className="p-6"><h2 className="text-2xl font-bold text-gray-800">Periodos Académicos</h2><p className="mt-4 text-gray-600">Seleccione una opción del menú</p></div>;
        }
    };

    return (
        <Layout
            menuItems={menuItems}
            activeMenu={activeMenu}
            setActiveMenu={setActiveMenu}
            onLogout={onLogout}
            userName={userName}
            userRole="Dirección Académica"
        >
            {renderContent()}
        </Layout>
    );
};

export default DashboardDireccion;

